'use client'

import Script from 'next/script'
import * as React from 'react'

import { useIsLoggedInQuery, useUserIdQuery } from 'auth/hooks'
import {
  initScript as googleSiteTagInitScript,
  scriptUrl as googleSiteTagScriptUrl,
  globalSiteInit,
} from '@repo/analytics/google-site-tag'
import {
  isIubendaBridgeReady,
  isIubendaPurposeAllowed,
  iubendaConsentEventName,
} from '@repo/analytics/iubenda'
import { useIubendaConfig } from './iubenda'
import { IubendaSnippet } from './iubenda-snippet'
import type { IubendaConfig } from './iubenda'

interface GlobalSiteTagProps {
  disableAds?: boolean
  /** Skip consent gating on domains where Iubenda is not configured. */
  enableIubenda?: boolean
}

const iubendaDisabledConfig: IubendaConfig = {
  scriptProps: {},
  variant: 'notApplicable' as const,
  gdprApplies: false,
}

interface IubendaPurposes {
  marketing: boolean
  measurement: boolean
}

interface IubendaPurposeState {
  gdprApplies?: boolean
  purposes: IubendaPurposes
}

const deniedIubendaPurposes: IubendaPurposes = { measurement: false, marketing: false }

const readIubendaPurposes = (): IubendaPurposes => ({
  measurement: isIubendaPurposeAllowed('4'),
  marketing: isIubendaPurposeAllowed('5'),
})

function useIubendaPurposes(gdprApplies?: boolean) {
  const [state, setState] = React.useState<IubendaPurposeState>(() => ({
    gdprApplies,
    purposes:
      gdprApplies && !isIubendaBridgeReady(true) ? deniedIubendaPurposes : readIubendaPurposes(),
  }))

  React.useEffect(() => {
    const updatePurposes = () => {
      setState({ gdprApplies, purposes: readIubendaPurposes() })
    }

    if (!gdprApplies || isIubendaBridgeReady(true)) {
      updatePurposes()
    }
    window.addEventListener(iubendaConsentEventName, updatePurposes)
    return () => window.removeEventListener(iubendaConsentEventName, updatePurposes)
  }, [gdprApplies])

  return state.gdprApplies === gdprApplies ? state.purposes : deniedIubendaPurposes
}

function useGlobalSiteTagInitialization({
  measurementAllowed,
  variant,
  waitForIubenda,
}: {
  measurementAllowed: boolean
  variant: IubendaConfig['variant']
  waitForIubenda: boolean
}) {
  const isLoggedInQuery = useIsLoggedInQuery()
  const userIdQuery = useUserIdQuery()
  const userId = isLoggedInQuery.data ? userIdQuery.data || null : null
  const initializedUserIdRef = React.useRef<string | null | undefined>(undefined)
  const canInitialize = measurementAllowed && isLoggedInQuery.isFetched

  const initialize = React.useCallback(() => {
    if (canInitialize && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      if (initializedUserIdRef.current === userId) {
        return true
      }

      globalSiteInit(userId)
      initializedUserIdRef.current = userId
      return true
    }

    return false
  }, [canInitialize, userId])

  React.useEffect(() => {
    if (variant !== 'locationNotSet') {
      initialize()
    }
  }, [initialize, variant])

  React.useEffect(() => {
    if (!waitForIubenda) {
      return
    }

    window.addEventListener(iubendaConsentEventName, initialize)
    return () => window.removeEventListener(iubendaConsentEventName, initialize)
  }, [initialize, waitForIubenda])

  return initialize
}

function UsGlobalSiteTagScripts({
  disableAds,
  scriptProps,
}: {
  disableAds: boolean
  scriptProps: IubendaConfig['scriptProps']
}) {
  return (
    <>
      <IubendaSnippet
        id="global-site-tag-script-init"
        purposes={scriptProps['data-iub-purposes']}
        defer={scriptProps['data-cf-defer']}
      >
        {googleSiteTagInitScript({ disableAds })}
      </IubendaSnippet>
      <IubendaSnippet
        id="global-site-tag-script"
        src={googleSiteTagScriptUrl}
        purposes={scriptProps['data-iub-purposes']}
        defer={scriptProps['data-cf-defer']}
      />
    </>
  )
}

function GlobalSiteTagScripts({
  disableAds,
  gdprApplies,
  usprApplies,
  initialize,
  measurementAllowed,
  scriptProps,
}: {
  disableAds: boolean
  gdprApplies?: boolean
  usprApplies?: boolean
  initialize: () => boolean
  measurementAllowed: boolean
  scriptProps: IubendaConfig['scriptProps']
}) {
  if (usprApplies) {
    return <UsGlobalSiteTagScripts disableAds={disableAds} scriptProps={scriptProps} />
  }
  const iubendaManaged = !gdprApplies && scriptProps.type === 'text/plain'
  const iubendaProps = gdprApplies ? {} : scriptProps
  const deferredSourceProps = scriptProps['data-cf-defer']
    ? { 'data-cf-src': googleSiteTagScriptUrl }
    : {}

  return (
    <>
      {/* The stub runs early so gtag('event', ...) calls fired before the remote
          script loads queue in dataLayer instead of being dropped; the heavy
          gtag/js download itself stays deferred to lazyOnload. */}
      <Script
        id="global-site-tag-script-init"
        strategy="afterInteractive"
        onReady={
          iubendaManaged || !measurementAllowed
            ? undefined
            : () => {
                initialize()
              }
        }
        {...iubendaProps}
      >
        {googleSiteTagInitScript({ disableAds })}
      </Script>
      <Script
        id="global-site-tag-script"
        src={googleSiteTagScriptUrl}
        strategy="lazyOnload"
        {...iubendaProps}
        {...(gdprApplies ? {} : deferredSourceProps)}
      />
    </>
  )
}

export const GlobalSiteTag = ({ disableAds = false, enableIubenda = true }: GlobalSiteTagProps) => {
  const resolvedIubendaConfig = useIubendaConfig()
  const config = enableIubenda ? resolvedIubendaConfig : iubendaDisabledConfig
  const purposes = useIubendaPurposes(config.gdprApplies)
  const measurementAllowed = !(config.gdprApplies || config.usprApplies) || purposes.measurement
  const tagAllowed = !config.gdprApplies || purposes.measurement || purposes.marketing
  const initialize = useGlobalSiteTagInitialization({
    measurementAllowed,
    variant: config.variant,
    waitForIubenda: !config.gdprApplies && config.scriptProps.type === 'text/plain',
  })
  const shouldRender = config.variant !== 'locationNotSet' && tagAllowed

  return shouldRender ? (
    <GlobalSiteTagScripts
      disableAds={disableAds}
      gdprApplies={config.gdprApplies}
      usprApplies={config.usprApplies}
      initialize={initialize}
      measurementAllowed={measurementAllowed}
      scriptProps={config.scriptProps}
    />
  ) : null
}
